import { c as _c } from "react/compiler-runtime";
import { type WippenSupportSpringsDesignTarget, type WippenTensionDesign } from '@/lib/piano/touch-design/design/wippen-support-springs-design';
import { useTranslation } from '@/hooks/use-translation';
import { useKeyboard } from '@/hooks/keyboard/use-keyboard';
import { formatWeightValue } from '@/lib/format/format-weight';
import { useCallback, useEffect, useMemo } from 'react';
import { WipenDesignSpecificationItem } from './WippenDesignSpecificationItem';

/**
 * `20,0 g`: the widest label the spring weight stepper can show. The value box
 * is sized from it so that stepping does not make the control jump.
 */
const SPRING_WEIGHT_CHARACTERS = 6;

/** `#88 · G#4`: likewise, the widest label of the tension drop stepper. */
const TENSION_DROP_INDEX_CHARACTERS = 9;
export interface WippenDesignSelectorProps {
  currentTarget: WippenSupportSpringsDesignTarget;
  onTargetChange: (target: WippenSupportSpringsDesignTarget) => void;
  shouldEnableTensionDropIndexSelector: boolean;
}
export const WippenDesignSelector: React.FC<WippenDesignSelectorProps> = t0 => {
  const $ = _c(59);
  const {
    currentTarget,
    onTargetChange,
    shouldEnableTensionDropIndexSelector
  } = t0;
  const {
    currentLanguage,
    t
  } = useTranslation();
  const {
    keyboard
  } = useKeyboard();
  const tensionDesign = currentTarget as WippenTensionDesign;
  let t1;
  if ($[0] !== shouldEnableTensionDropIndexSelector || $[1] !== tensionDesign?.numberOfSprings || $[2] !== tensionDesign?.springBalanceWeight || $[3] !== tensionDesign?.tensionDropIndex) {
    t1 = shouldEnableTensionDropIndexSelector ? {
      numberOfSprings: tensionDesign?.numberOfSprings,
      springBalanceWeight: tensionDesign?.springBalanceWeight,
      tensionDropIndex: tensionDesign?.tensionDropIndex
    } : {
      numberOfSprings: tensionDesign?.numberOfSprings,
      springBalanceWeight: tensionDesign?.springBalanceWeight
    };
    $[0] = shouldEnableTensionDropIndexSelector;
    $[1] = tensionDesign?.numberOfSprings;
    $[2] = tensionDesign?.springBalanceWeight;
    $[3] = tensionDesign?.tensionDropIndex;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const currentTensionDesign = t1;
  let t2;
  if ($[5] !== currentTensionDesign || $[6] !== onTargetChange) {
    t2 = numberOfSprings => {
      onTargetChange({
        ...currentTensionDesign,
        numberOfSprings
      });
    };
    $[5] = currentTensionDesign;
    $[6] = onTargetChange;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const onSpringsCountChange = t2;
  let t3;
  if ($[8] !== currentTensionDesign || $[9] !== onTargetChange) {
    t3 = springBalanceWeight => {
      onTargetChange({
        ...currentTensionDesign,
        springBalanceWeight
      });
    };
    $[8] = currentTensionDesign;
    $[9] = onTargetChange;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const onSpringBalanceWeightChange = t3;
  let t4;
  if ($[11] !== currentTensionDesign || $[12] !== onTargetChange) {
    t4 = tensionDropIndex => {
      onTargetChange({
        ...currentTensionDesign,
        tensionDropIndex
      });
    };
    $[11] = currentTensionDesign;
    $[12] = onTargetChange;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const onTensionDropIndexChange = t4;
  let t5;
  if ($[14] !== currentTensionDesign.numberOfSprings || $[15] !== currentTensionDesign.tensionDropIndex || $[16] !== onTensionDropIndexChange) {
    t5 = () => {
      if (currentTensionDesign.tensionDropIndex !== undefined && currentTensionDesign.tensionDropIndex > currentTensionDesign.numberOfSprings) {
        onTensionDropIndexChange(currentTensionDesign.numberOfSprings);
      }
    };
    $[14] = currentTensionDesign.numberOfSprings;
    $[15] = currentTensionDesign.tensionDropIndex;
    $[16] = onTensionDropIndexChange;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== currentTensionDesign || $[19] !== onTensionDropIndexChange) {
    t6 = [currentTensionDesign, onTensionDropIndexChange];
    $[18] = currentTensionDesign;
    $[19] = onTensionDropIndexChange;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  useEffect(t5, t6);
  let t7;
  if ($[21] !== currentLanguage || $[22] !== t) {
    t7 = value => t("{{value}} g", {
      value: formatWeightValue(value, currentLanguage)
    });
    $[21] = currentLanguage;
    $[22] = t;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const springBalanceWeightLabelFormatter = t7;
  let t8;
  if ($[24] !== keyboard || $[25] !== t) {
    t8 = value_0 => t("#{{number}} \xB7 {{note}}", {
      note: keyboard.getKeyByNumber(value_0)?.name ?? "",
      number: value_0
    });
    $[24] = keyboard;
    $[25] = t;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const tensionDropIndexLabelFormatter = t8;
  let t9;
  if ($[27] !== t) {
    t9 = t("Number of springs");
    $[27] = t;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== keyboard.size) {
    t10 = keyboard.size.toString();
    $[29] = keyboard.size;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== currentTensionDesign.numberOfSprings || $[32] !== keyboard.size || $[33] !== onSpringsCountChange || $[34] !== t10.length) {
    t11 = {
      maxCharacters: t10.length,
      maxValue: keyboard.size,
      minValue: 0,
      onChange: onSpringsCountChange,
      value: currentTensionDesign.numberOfSprings
    };
    $[31] = currentTensionDesign.numberOfSprings;
    $[32] = keyboard.size;
    $[33] = onSpringsCountChange;
    $[34] = t10.length;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t11 || $[37] !== t9) {
    t12 = <WipenDesignSpecificationItem name={t9} selectorProps={t11} />;
    $[36] = t11;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t) {
    t13 = t("Weight supported by the springs");
    $[39] = t;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== currentTensionDesign.springBalanceWeight || $[42] !== onSpringBalanceWeightChange || $[43] !== springBalanceWeightLabelFormatter) {
    t14 = {
      labelFormatter: springBalanceWeightLabelFormatter,
      maxCharacters: SPRING_WEIGHT_CHARACTERS,
      maxValue: 20,
      minValue: 0,
      onChange: onSpringBalanceWeightChange,
      step: 0.5,
      value: currentTensionDesign.springBalanceWeight
    };
    $[41] = currentTensionDesign.springBalanceWeight;
    $[42] = onSpringBalanceWeightChange;
    $[43] = springBalanceWeightLabelFormatter;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t13 || $[46] !== t14) {
    t15 = <WipenDesignSpecificationItem name={t13} selectorProps={t14} />;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== currentTensionDesign.numberOfSprings || $[49] !== currentTensionDesign.tensionDropIndex || $[50] !== onTensionDropIndexChange || $[51] !== shouldEnableTensionDropIndexSelector || $[52] !== t || $[53] !== tensionDropIndexLabelFormatter) {
    t16 = shouldEnableTensionDropIndexSelector ? <WipenDesignSpecificationItem name={t("Tension drop index")} selectorProps={{
      labelFormatter: tensionDropIndexLabelFormatter,
      maxCharacters: TENSION_DROP_INDEX_CHARACTERS,
      maxValue: currentTensionDesign.numberOfSprings,
      minValue: 1,
      onChange: onTensionDropIndexChange,
      value: currentTensionDesign.tensionDropIndex
    }} /> : null;
    $[48] = currentTensionDesign.numberOfSprings;
    $[49] = currentTensionDesign.tensionDropIndex;
    $[50] = onTensionDropIndexChange;
    $[51] = shouldEnableTensionDropIndexSelector;
    $[52] = t;
    $[53] = tensionDropIndexLabelFormatter;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] !== t12 || $[56] !== t15 || $[57] !== t16) {
    t17 = <div className="flex flex-wrap gap-x-8 gap-y-3.5">{t12}{t15}{t16}</div>;
    $[55] = t12;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  return t17;
};